import * as React from "react"
import { connect } from "react-redux"
import trans from "trans"

import _forEach from "lodash/forEach";

import {
    css,
    DataTable,
    DataBody,
    DataRow,
    DataHeader,
    DataCell,
    Theme,
    Icon,
} from "@bdfx/mergado-ui-kit"

import {
    isGodModeOn,
    isLocalhost,
} from "app/helpers/api";

import DataCellSortable, {
    ImagesTable,
    ImageValidationsTable,
    ImageWatermarksTable,
    ImageQualityTable,
    NoTable,
    ProductElementsTable,
    ProductsTable
} from "./dataCellSortable";

import { FiltersFormType } from "../types/filtertypes";
import { ViewTableType } from "products/containers";
import ProductTableRow from "./product-table-row";
import ValidationRow from "./validation-row";
import WatermarkRow from "./watermark-row";
import ImageQualityRow from "./image-quality-row";
import SectionLoader from "app/components/section-loader";

import {
    allowObjectToImageRatioForUsers,
    allowSuperadminForUsers,
} from "template-edit/components/helper";

import { Project } from "app/types/model-types";
import { SetByUser } from "./product-table";
import { ExtendedProductType, ProductGeneral } from "../types";
import DoubleScrollBar from "app/components/double-scroll-bar";
import { selectFormValues } from "app/reducers/form";
import { ShowGroupType } from "diagnostics/diagnostics";
import VisualSimilarityTableRow from "./visual-similarity-table-row";
import { removeEmpty } from "shape-edit/forms/helpers";

interface Props {
    data: ProductGeneral
    isLoading: boolean
    viewTable: ViewTableType
    project: Project
    paginator: JSX.Element
    handleSortBy: Function
    filtersForm: FiltersFormType
    publishSelected: Function
    changeSetByUser: Function
    showModal: Function
    getEmptyFiltersForm: Function
    updateWatermarkMasksFromDb: Function
}

interface State {
    copiedToClipboard: number | null  //ID of element which was copied into clipboard
}

class ProductTableTable extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            copiedToClipboard: null,
        }
    }

    copyToClipboard = (e, id) => {
        let copyText = document.getElementById('mergado_query_' + id);

        let range, select;

        if (document.createRange) {
            range = document.createRange();
            range.selectNode(copyText)
            select = window.getSelection();
            select.removeAllRanges();
            select.addRange(range);
            document.execCommand('copy');
            select.removeAllRanges();
            this.setState({ copiedToClipboard: id })
        }

        e.target.focus();
    }

    render() {
        const { data, isLoading, viewTable, project, paginator, filtersForm } = this.props
        const { sortBy } = filtersForm

        const userId = project.user && project.user.id ? project.user.id : null
        const groupNames: ShowGroupType[] = filtersForm.image && filtersForm.image.showGroup && filtersForm.image.showGroup ? filtersForm.image.showGroup : ['all']

        const emptyImages = groupNames.includes('empty_images')
        const sameImages = groupNames.includes('same_images')
        const invalidUrl = groupNames.includes('invalid_url')

        const statusCodeHeader = <DataCellSortable table={ImagesTable} sortBy={sortBy} column={'status_code'} title={'status_code'} isNumeric={true} handleSortBy={this.props.handleSortBy} />

        let showAlternativeImages = false
        if (data && data.data) {
            _forEach(data.data, (e: ExtendedProductType) => {
                if (e.alternative_images && e.alternative_images.length > 0) {
                    showAlternativeImages = true
                    return true
                }
            })
        }

        const isAllowSpecialFields = (allowObjectToImageRatioForUsers.includes(userId) || isGodModeOn)


        const productImageHeader = <React.Fragment>
            <DataCell type={"header"} key={'image'} className='centered'>{trans('product_image')}</DataCell>
            {showAlternativeImages &&
                <DataCell
                    type={"header"}
                    key={'alternative_images'}
                    className='centered'>
                    {trans('alternative_images')}
                </DataCell>
            }

            <DataCell>{trans('background')}</DataCell>

            {!emptyImages && isAllowSpecialFields &&
                <DataCellSortable className={'some-users-only'} table={ImagesTable} title={'object_to_image_ratio'} sortBy={sortBy} column={'object_to_image_ratio'} isNumeric={true} handleSortBy={this.props.handleSortBy} maxWidth={20} />
            }
        </React.Fragment>

        const elementsHeader = <React.Fragment>
            <DataCellSortable table={ProductElementsTable} sortBy={sortBy} column={'name'} handleSortBy={this.props.handleSortBy} />
            <DataCellSortable table={ProductElementsTable} sortBy={sortBy} column={'price_vat'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
            <DataCellSortable table={ProductElementsTable} sortBy={sortBy} column={'categorytext'} handleSortBy={this.props.handleSortBy} />
            <DataCellSortable table={ProductElementsTable} sortBy={sortBy} column={'manufacturer'} handleSortBy={this.props.handleSortBy} />
        </React.Fragment>

        const templateHeader = <DataCellSortable table={ProductsTable} sortBy={sortBy} column={'template_id'} title={'product_template'} handleSortBy={this.props.handleSortBy} />

        const imageInfoHeader = <React.Fragment>
            <DataCellSortable table={ImagesTable} sortBy={sortBy} column={'width'} title={'input_width'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
            <DataCellSortable table={ImagesTable} sortBy={sortBy} column={'height'} title={'input_height'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
            <DataCellSortable table={ImagesTable} sortBy={sortBy} column={'content_type'} title={'product_content_type'} handleSortBy={this.props.handleSortBy} />
            <DataCellSortable table={ImagesTable} sortBy={sortBy} column={'content_length'} title={'product_content_length'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
            <DataCellSortable table={ProductsTable} sortBy={sortBy} column={'rendered_size'} title={'rendered_content_length'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
            <DataCellSortable table={ProductsTable} sortBy={sortBy} column={'rendered_compression'} title={'rendered_compression'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
            {/* <DataCellSortable table={ProductsTable} sortBy={sortBy} column={'rendered_sensitivity'} title={'rendered_sensitivity'} isNumeric={false} handleSortBy={this.props.handleSortBy} /> */}
            <DataCell style={{ whiteSpace: 'pre' }}>{trans('not_recommended_colorspace')}</DataCell>
        </React.Fragment>

        const sameImagesHeader = <React.Fragment>
            <DataCell type={"header"} key={'same_images'} className='centered'>{trans('status_same_images')}</DataCell>
            <DataCellSortable table={NoTable} sortBy={sortBy} column={'total'} title={'total_products'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
        </React.Fragment>

        const productLinksHeader = <DataCell type={"header"} key={'links'} className='centered'>{trans('product_links')}</DataCell>

        if (isLoading) {
            return <SectionLoader />
        }

        const bulkActions = viewTable === 'watermarks' ? [
            { icon: <Icon type="eraser" />, type: "eraser", action: () => { console.debug('debug: selected watermarks') } },
        ] : viewTable === 'products' && isAllowSpecialFields ? [
            { icon: <Icon type="download-light" />, type: "publish-downloader", action: () => this.props.publishSelected('downloader') },
            { icon: <Icon type="eraser" />, type: "publish-bgremover", action: () => this.props.publishSelected('bgremover') },
            { icon: <Icon type="copy-light" />, type: "publish-composer", action: () => this.props.publishSelected('composer') },
        ] : []

        return (
            <StyledGroup key={viewTable}>
                <DoubleScrollBar color={Theme.blue}>
                    <DataTable
                        className={'products-table' + (isLoading ? ' muk-helper-loading' : '')}
                        onRowSelected={(e) => { }}
                        buttons={[]}
                    /*bulkActions={bulkActions} */
                    >
                        {viewTable === 'products' &&
                            <DataHeader className={'nowrap unselectable'}>

                                {!emptyImages &&
                                    statusCodeHeader
                                }

                                {!emptyImages &&
                                    productImageHeader
                                }

                                {invalidUrl &&
                                    <DataCell type={"header"} key={'invalid_url'} className='centered'>{trans('status_invalid_url')}</DataCell>
                                }

                                {elementsHeader}

                                {templateHeader}

                                {sameImages &&
                                    sameImagesHeader
                                }

                                {!emptyImages && !invalidUrl && !sameImages &&
                                    imageInfoHeader
                                }

                                {productLinksHeader}
                            </DataHeader>
                        }

                        {viewTable === 'validations' &&
                            <DataHeader>
                                {isGodModeOn &&
                                    <DataCell type={"header"} key={'image_id'}>{trans('image_id')}</DataCell>
                                }

                                <DataCell type={"header"} key={'product_image'}>{trans('product_image')}</DataCell>
                                {showAlternativeImages &&
                                    <DataCell
                                        type={"header"}
                                        key={'alternative_images'}
                                        className='centered'>
                                        {trans('alternative_images')}
                                    </DataCell>
                                }

                                <DataCellSortable table={ProductElementsTable} sortBy={sortBy} column={'name'} handleSortBy={this.props.handleSortBy} maxWidth={20} />
                                <DataCellSortable table={ImageValidationsTable} sortBy={sortBy} column={'status'} title={'status'} isNumeric={false} handleSortBy={this.props.handleSortBy} />
                                {userId && (allowSuperadminForUsers.includes(userId)) &&
                                    <DataCellSortable className={'some-users-only'} table={ImageValidationsTable} sortBy={sortBy} column={'status_reason'} title={'status_reason'} isNumeric={false} handleSortBy={this.props.handleSortBy} />
                                }
                                <DataCellSortable table={ImageValidationsTable} sortBy={sortBy} column={'correctness_percentage_offset'} title={'correctness_percentage_offset'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
                                <DataCellSortable table={ImageValidationsTable} sortBy={sortBy} column={'has_alpha'} title={'has_alpha'} isNumeric={false} handleSortBy={this.props.handleSortBy} />

                                {isAllowSpecialFields && <React.Fragment>
                                    <DataCellSortable className={'some-users-only'} table={ImageValidationsTable} sortBy={sortBy} column={'empty_background_probability'} title={'empty_background_probability'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
                                    <DataCellSortable className={'some-users-only'} table={ImageValidationsTable} sortBy={sortBy} column={'object_to_image_ratio'} title={'object_to_image_ratio'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
                                    <DataCellSortable className={'some-users-only'} table={ImageValidationsTable} sortBy={sortBy} column={'object_bounding_box_to_image_ratio'} title={'object_bounding_box_to_image_ratio'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
                                </React.Fragment>}

                                {productLinksHeader}
                            </DataHeader>
                        }

                        {viewTable === 'watermarks' &&
                            <DataHeader>
                                {isGodModeOn &&
                                    <DataCell type={"header"} key={'image_id'}>{trans('image_id')}</DataCell>
                                }

                                <DataCell type={"header"} key={'product_image'}>{trans('product_image')}</DataCell>
                                <DataCellSortable table={ProductElementsTable} sortBy={sortBy} column={'name'} handleSortBy={this.props.handleSortBy} />
                                <DataCellSortable table={ImageWatermarksTable} sortBy={sortBy} column={'status'} title={'status'} isNumeric={false} handleSortBy={this.props.handleSortBy} />

                                {userId && (allowSuperadminForUsers.includes(userId)) &&
                                    <DataCellSortable className={'some-users-only'} table={ImageValidationsTable} sortBy={sortBy} column={'status_reason'} title={'status_reason'} isNumeric={false} handleSortBy={this.props.handleSortBy} />
                                }

                                <DataCellSortable table={ImageWatermarksTable} sortBy={sortBy} column={'confidence'} title={'probability'} isNumeric={true} handleSortBy={this.props.handleSortBy} />
                                {productLinksHeader}
                            </DataHeader>
                        }

                        {viewTable === 'images_quality' &&
                            <DataHeader>
                                {(isGodModeOn || isLocalhost) &&
                                    <DataCell type={"header"} key={'image_id'}>{trans('image_id')}</DataCell>
                                }

                                <DataCell type={"header"} key={'product_image'}>{trans('product_image')}</DataCell>
                                <DataCellSortable table={ProductElementsTable} sortBy={sortBy} column={'name'} handleSortBy={this.props.handleSortBy} />
                                <DataCellSortable table={ImageQualityTable} sortBy={sortBy} column={'status'} title={'status'} isNumeric={false} handleSortBy={this.props.handleSortBy} />

                                {userId && (allowSuperadminForUsers.includes(userId)) &&
                                    <DataCell type={"header"} key={'status_reason'}>{trans('status_reason')}</DataCell>
                                }

                                <DataCellSortable table={ImageQualityTable} sortBy={sortBy} column={'confidence'} title={'probability'} isNumeric={true} handleSortBy={this.props.handleSortBy} />

                                {productLinksHeader}
                            </DataHeader>
                        }

                        {viewTable === 'visual_similarity' &&
                            <DataHeader>
                                {(isGodModeOn || isLocalhost) &&
                                    <DataCell type={"header"} key={'image_id'}>{trans('image_id')}</DataCell>
                                }
                                {!emptyImages &&
                                    statusCodeHeader
                                }
                                <DataCell type={"header"}>{trans('product_image')}</DataCell>
                                <DataCellSortable table={ProductElementsTable} sortBy={sortBy} column={'name'} handleSortBy={this.props.handleSortBy} />
                                <DataCell type={"header"}>{trans('template')}</DataCell>
                                <DataCell type={"header"} width={'50%'}>{trans('similar_images')}</DataCell>

                                {productLinksHeader}
                            </DataHeader>
                        }

                        {!isLoading &&
                            <DataBody>
                                {viewTable === 'visual_similarity' && data.data && data.data.map((u, idx) => {
                                    const productImageKey = `${u.product_id}_${u.image_id}`
                                    return <VisualSimilarityTableRow
                                        key={productImageKey}
                                        product={u}
                                        groupNames={groupNames}
                                        copyToClipboard={this.copyToClipboard}
                                        copiedToClipboard={this.state.copiedToClipboard}
                                        showModal={this.props.showModal}
                                        idx={idx}
                                        getEmptyFiltersForm={this.props.getEmptyFiltersForm}
                                        showAlternativeImages={showAlternativeImages}
                                        userId={userId}
                                    />
                                })
                                }

                                {viewTable === 'products' && data.data && data.data.map((u, idx) => {
                                    const productImageKey = `${u.product_id}_${u.image_id}`
                                    return <ProductTableRow
                                        key={productImageKey}
                                        product={u}
                                        groupNames={groupNames}
                                        copyToClipboard={this.copyToClipboard}
                                        copiedToClipboard={this.state.copiedToClipboard}
                                        showModal={this.props.showModal}
                                        idx={idx}
                                        getEmptyFiltersForm={this.props.getEmptyFiltersForm}
                                        showAlternativeImages={showAlternativeImages}
                                        userId={userId}
                                    />
                                })}

                                {viewTable === 'validations' && data.data && data.data.map((u, idx) => {
                                    const productImageKey = `${u.product_id}_${u.image_id}`

                                    return <ValidationRow
                                        key={productImageKey}
                                        image={u}
                                        showModal={this.props.showModal}
                                        idx={idx}
                                        changeSetByUser={this.props.changeSetByUser}
                                        getEmptyFiltersForm={this.props.getEmptyFiltersForm}
                                        userId={userId}
                                    />
                                })}

                                {viewTable === 'watermarks' && data.data && data.data.map((u, idx) => {
                                    const productImageKey = `${u.product_id}_${u.image_id}`
                                    return <WatermarkRow
                                        key={`${productImageKey}_${u.masks_group_id}_${u.watermarks_user_set}`}
                                        product={u}
                                        showModal={this.props.showModal}
                                        idx={idx}
                                        changeSetByUser={this.props.changeSetByUser}
                                        getEmptyFiltersForm={this.props.getEmptyFiltersForm}
                                        updateWatermarkMasksFromDb={this.props.updateWatermarkMasksFromDb}
                                        showAlternativeImages={false /* showAlternativeImages */}   /* TODO */
                                        userId={userId}
                                    />
                                })}

                                {viewTable === 'images_quality' && data.data && data.data.map((u, idx) => {
                                    const productImageKey = `${u.product_id}_${u.image_id}`
                                    return <ImageQualityRow
                                        key={productImageKey}
                                        image={u}
                                        showModal={this.props.showModal}
                                        idx={idx}
                                        changeSetByUser={this.props.changeSetByUser}
                                        getEmptyFiltersForm={this.props.getEmptyFiltersForm}
                                        userId={userId}
                                    />
                                })}
                            </DataBody>
                        }
                    </DataTable>
                </DoubleScrollBar>

                <div style={{ textAlign: 'center', marginTop: '10px' }}>{paginator}</div>
            </StyledGroup>
        )
    }
}

const StyledGroup = css('div')({
    " .products-table": {
        display: 'block',
        /*overflowX: 'auto',*/
        color: '#fff',
        " thead td.muk-datacell": {
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '.9em',
            fontWeight: 'bold',
        },
        " thead th": {
            position: 'sticky',
            top: 0,
            background: "inherit",
            zIndex: 5,
            boxShadow: "0px 2px 5px 0 rgba(0,0,0,0.35)",
        },
        '.muk-helper-loading': {
            width: '100% !important',
        },
        ' ul.product-list': {
            listStyle: 'none',
            ' .copy-to-clipboard': {
                display: 'flex',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                ':hover': {
                    backgroundColor: 'rgba(200,200,200, .2)',
                },
                '.copied': {
                    backgroundColor: 'rgba(200,200,200, .2)',
                },
            },
        },
        ' .mergado-list': {
            display: 'inline-block',
            paddingLeft: '20px',
            cursor: 'pointer',
            fontSize: '12px',
            lineHeight: '24px',
        },
        ' .id-list': {
            paddingLeft: '10px',
            maxWidth: '300px',
            maxHeight: '3em',
            fontSize: '12px',
            lineHeight: '24px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        },
        ' .date': {
            whiteSpace: 'nowrap',
        },
        ' .productItemAndNo': {
            display: 'block',
            color: '#444',
        },
    },
    ' .centered': {
        textAlign: 'center',
    },
    ' .right': {
        textAlign: 'right',
        paddingRight: '15px',
    },
    ' .clickable': {
        cursor: 'pointer',
    },
    ' .nowrap': {
        whiteSpace: 'nowrap',
    },
    ' .product-links': {
        lineHeight: '50px',
        ' .muk-gridcell': {
            padding: '4px',
        },
    },
    ' .header-cell-active': {
        background: '#5c5c5c',
    },
    ' th': {
        textAlign: 'center',
    },
})

export const StyledDataRow = css(DataRow)({
    ' td': {
        textAlign: 'center',
    },
    ' .left': {
        textAlign: 'left',
    },
    ' .status': {
        whiteSpace: 'nowrap',
        textAlign: 'left',
    },
    ' .m-field-muk-select': {
        marginBottom: 0,
    },
    ' .react-select-item-container': {
        minWidth: '200px',
        whiteSpace: 'nowrap',
        paddingLeft: '20px',
    },
    ' .row-watermark-box': {
        borderBottom: '3px solid #ccc',
        borderRadius: Theme.radius,
        padding: '0 4px',
    },
})

function mapStateToProps(state) {
    return {
        project: state.project.data,
        filtersForm: selectFormValues(state, 'filtersForm'),
    }
}

export default connect(mapStateToProps, {
})(ProductTableTable)
